import { onAuthStateChanged } from 'firebase/auth';
import { createContext, FC, ReactNode, useCallback, useContext, useEffect, useRef, useState } from 'react';

import { Experiment, type ExperimentClient, type Exposure } from '@amplitude/experiment-js-client';
import { getUser } from '@api/user-api';
import { NEXT_PUBLIC_AMPLITUDE_EXPERIMENTS_KEY } from '@env';
import { analytics } from '@modules/analytics';
import { auth, type FirebaseUser } from '@modules/firebase';

import { generateAnonymousId } from './analytics/helpers';
import { logger } from './logger';
import { blinqDatadogRum } from './monitoring/rum';

export interface FeatureFlagVariant {
  example: 'control' | 'variant';
  'outlook-contact-sync': 'control' | 'treatment';
  'assign-multiple-profiles-for-cards': 'off' | 'on';
  'quick-create-cards-templates': 'off' | 'on';
  'onboarding-paywall-uplift': 'control' | 'treatment';
  'hide-business-onboarding-interval-toggle': 'on' | 'off';
  'create-template-from-card': 'on' | 'off';
  'salesforce-contact-export': 'on' | 'off';
  'targeted-paywall': 'control' | 'treatment';
  'individual-onboarding-wizard-iteration-3': 'control' | 'treatment';
  'business-onboarding-iteration-1': 'on' | 'off';
  blinq_logo_in_qr: 'control' | 'treatment';
  'integrations-glow-up': 'on' | 'off';
  ios_download_app: 'control' | 'treatment';
  qr_link_subdomains: 'control' | 'share' | 'share1' | 'share2' | 'share3' | 'fallback';
  'new-team-dash-home': 'off' | 'on';
  'contact-fuzzy-search': 'off' | 'on';
  'contact-manual-add': 'off' | 'on';
  'share-contact-with-business': 'off' | 'on';
  'web-ia-new-templates-landing-page': 'off' | 'on';
  schrodinger_delete_contact: 'off' | 'on';
  'web-ia-new-team-cards-page': 'off' | 'on';
  schrodinger_edit_card: 'off' | 'on';
  'web-ia-contacts-page-redesign': 'off' | 'on';
  'web-ia-team-members-page-redesign': 'off' | 'on';
  'web-biz-enhanced-team-cards': 'treatment' | 'control';
  schrodinger_virtual_background_image: 'off' | 'on';
  schrodinger_virtual_background_delete: 'off' | 'on';
  schrodinger_create_update_user_doc: 'off' | 'on';
  'web-contact-details-page-redesign': 'off' | 'on';
  'increase-contact-tag-limit-to-100': 'off' | 'on';
  'web-contact-tags-v222': 'off' | 'on';
  'dynamic-link-service-webapp': 'off' | 'on';
  'web-contact-tags-v223': 'off' | 'on';
}

interface FeatureFlagMetaData {
  /**
   * Waits for client to initialize and fetch the flag
   */
  loading: boolean;
  /**
   * Exposes the user to the flag, will not fire unless state is ready.
   */
  expose: () => void;
}

const client = Experiment.initialize(NEXT_PUBLIC_AMPLITUDE_EXPERIMENTS_KEY, {
  automaticExposureTracking: false,
  exposureTrackingProvider: {
    track(exposure: Exposure) {
      analytics.track({
        name: '$exposure',
        properties: {
          flag_key: exposure.flag_key,
          variant: exposure.variant,
          experiment_key: exposure.experiment_key,
        },
      });
      blinqDatadogRum.addFeatureFlagEvaluation(exposure.flag_key, exposure.variant);
    },
  },
});

export function isExperimentRunning<T extends keyof FeatureFlagVariant>(
  experimentName: T,
  variant: FeatureFlagVariant[T],
  fallback: boolean = false
): boolean {
  if (!client || !client.getUser()) {
    console.warn('Experiment client is not initialized.');
    return fallback;
  }
  return variant === client.variant(experimentName).value;
}

const fetchExperimentUser = async (firebaseUser: FirebaseUser | null) => {
  // TODO: Switch to apollo when effective workspace is ready
  const userDetails = firebaseUser ? await getUser(firebaseUser.uid) : undefined;

  if (userDetails) {
    return {
      user_id: userDetails.userId,
      device_id: generateAnonymousId(),
      user_properties: {
        workspaces: userDetails.bbOrgId || userDetails.userId,
        ...(userDetails.signInEmailAddress ? { email: userDetails.signInEmailAddress } : {}),
      },
    };
  }
  return {
    device_id: generateAnonymousId(),
  };
};

type ExperimentClientState =
  | {
      client: ExperimentClient;
      state: 'ready';
    }
  | {
      client?: never;
      state: 'initializing' | 'error';
    };

const AmplitudeExperimentsContext = createContext<ExperimentClientState>(undefined!);

export const AmplitudeExperimentsProvider: FC<{
  children: ReactNode;
}> = ({ children }) => {
  const [clientState, setClientState] = useState<ExperimentClientState>({
    state: 'initializing',
  });

  useEffect(() => {
    const initializeExperimentClient = async () => {
      try {
        const currentUser = auth.currentUser;
        const experimentUser = await fetchExperimentUser(currentUser);
        // Start with current user
        await client.start(experimentUser);

        setClientState({
          client,
          state: 'ready',
        });
      } catch (initError) {
        setClientState({
          state: 'error',
        });
        logger.error('Failed to initialize Amplitude Experiments client', { initError });
      }
    };
    initializeExperimentClient();
  }, [setClientState]);

  return (
    <AmplitudeExperimentsContext.Provider value={clientState}>
      {children}
    </AmplitudeExperimentsContext.Provider>
  );
};

export function useFeatureFlag<T extends keyof FeatureFlagVariant>(
  name: T,
  fallback: FeatureFlagVariant[T],
  /* Whether or not to automatically fire an exposure event when this hook is used.
   * This is useful when you want evaluate a flag early, and only want to expose in certain situations
   * make sure not to use the variant as a condition for exposure, unset === true
   */
  autoExpose?: boolean
): [FeatureFlagVariant[T], FeatureFlagMetaData] {
  const { state, client } = useContext(AmplitudeExperimentsContext);
  const [variant, setVariant] = useState<FeatureFlagVariant[T]>(fallback);
  const [isRefetching, setIsRefetching] = useState(true);
  const abortControllerRef = useRef<AbortController | null>(null);
  const shouldAutoExpose = typeof autoExpose === 'undefined' ? true : autoExpose;

  const loading = state === 'initializing' || isRefetching;

  const expose = useCallback(() => {
    if (state === 'ready') {
      client.exposure(name);
    }
  }, [state, client, name]);

  const fetchAndUpdateVariant = useCallback(
    async (client: ExperimentClient, user: FirebaseUser | null) => {
      // Abort any ongoing fetch, prevent race conditions from multiple fetches
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }

      // Create a new AbortController for the new fetch
      const abortController = new AbortController();
      abortControllerRef.current = abortController;

      // No user to update, try stored value
      if (!user) {
        const { value: storedValue } = client.variant(name, fallback);
        if (storedValue) {
          if (shouldAutoExpose) client.exposure(name);
          setVariant(storedValue as FeatureFlagVariant[T]);
          setIsRefetching(false);
          return;
        }
      }

      // Otherwise refetch with user

      try {
        const experimentUser = await fetchExperimentUser(user ?? auth.currentUser);
        await client.fetch(experimentUser);

        // Check if the fetch was aborted
        if (abortController.signal.aborted) return;

        const { value } = client.variant(name, fallback);
        if (shouldAutoExpose) client.exposure(name);
        setVariant(value as FeatureFlagVariant[T]);
      } catch (error) {
        if (!abortController.signal.aborted) {
          logger.error('Error fetching and updating variant:', { error });
          if (shouldAutoExpose) client.exposure(name);
          setVariant(fallback);
        }
      } finally {
        if (!abortController.signal.aborted) {
          setIsRefetching(false);
        }
      }
    },
    [name, fallback, shouldAutoExpose]
  );

  useEffect(() => {
    // Handle error state
    if (state === 'error') {
      setIsRefetching(false);
      return;
    }

    if (state !== 'ready') return;
    fetchAndUpdateVariant(client, null);

    const unsubscribe = onAuthStateChanged(auth, (user) => {
      if (!user) {
        client.clear();
      }
      fetchAndUpdateVariant(client, user);
    });

    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
      unsubscribe();
    };
  }, [client, state, fetchAndUpdateVariant]);

  return [variant, { loading, expose }];
}
